import React, { useCallback, useEffect, useState } from 'react';
import { BillingStatementReturn } from '../clients/rust-api-client';
import { billingService } from '../clients/ApiClients';
import { Badge } from '../common/Catalyst/badge';
import { Button } from '../common/Catalyst/button';
import { Input } from '../common/Catalyst/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../common/Catalyst/table';
import { Subheading } from '../common/Catalyst/heading';
import { Text } from '../common/Catalyst/text';
import LoadingSpinner from '../LoadingSpinner';
import ShowAlert from '../ShowAlert';
import {
  billingErrorMessage,
  compareMoney,
  formatBillingDate,
  formatMoney,
  savePdfResponse,
  signedAmountParts,
} from '../utils/billingHelpers';
import { ledgerEntryType } from './enumLabels';

interface Props {
  homeId: string;
}

/** Default range: the start of the year to today. */
const defaultFrom = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-01-01`;
};

/**
 * A statement over a date range.
 *
 * **Three balances, and all of them are true.** The opening and closing
 * balances belong to the range; today's balance belongs to now. On a
 * backdated statement they differ — and the screen must not pick one,
 * because the closing balance is what the statement says and today's
 * balance is what the homeowner owes. Saying so is the difference
 * between a manager reading it correctly and a phone call.
 */
const StatementPanel: React.FC<Props> = ({ homeId }) => {
  const [from, setFrom] = useState(defaultFrom());
  const [to, setTo] = useState('');
  const [statement, setStatement] = useState<BillingStatementReturn | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await billingService.getStatement({
        homeId,
        from: from || undefined,
        to: to || undefined,
      });
      setStatement(data);
    } catch (err) {
      console.error('Error loading statement:', err);
      setError(billingErrorMessage(err, "Couldn't load the statement."));
    } finally {
      setLoading(false);
    }
  }, [homeId, from, to]);

  useEffect(() => {
    void load();
  }, [load]);

  const download = async (): Promise<void> => {
    try {
      setDownloading(true);
      const response = await billingService.statementPdf(
        { homeId, from: from || undefined, to: to || undefined },
        { responseType: 'blob' as const }
      );
      savePdfResponse(response, `statement-${homeId}.pdf`);
    } catch (err) {
      console.error('Error downloading statement:', err);
      setError(billingErrorMessage(err, "Couldn't download the statement."));
    } finally {
      setDownloading(false);
    }
  };

  const movedSince =
    statement != null &&
    compareMoney(statement.closing_balance, statement.current_balance) !== 0;

  return (
    <div>
      {error && (
        <div className="mb-4">
          <ShowAlert
            alertType="error"
            alertTitle="Statement"
            alertMessage={error}
            isVisible={!!error}
            onClose={() => setError(null)}
            timeout={12000}
            isInline
          />
        </div>
      )}

      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1">
              From
            </label>
            <Input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="w-40"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1">
              To
            </label>
            <Input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="w-40"
            />
          </div>
        </div>
        <Button color="sky" onClick={download} disabled={downloading}>
          {downloading ? 'Preparing…' : 'Download PDF'}
        </Button>
      </div>

      {loading && !statement ? (
        <LoadingSpinner />
      ) : statement ? (
        <>
          <div className="mt-6 grid gap-6 sm:grid-cols-3">
            <div>
              <div className="text-sm text-zinc-500 dark:text-zinc-400">
                Balance on {formatBillingDate(statement.from)}
              </div>
              <div className="mt-1 text-lg text-zinc-900 dark:text-white">
                {formatMoney(statement.opening_balance)}
              </div>
            </div>
            <div>
              <div className="text-sm text-zinc-500 dark:text-zinc-400">
                Balance on {formatBillingDate(statement.to)}
              </div>
              <div className="mt-1 text-lg text-zinc-900 dark:text-white">
                {formatMoney(statement.closing_balance)}
              </div>
            </div>
            <div>
              <div className="text-sm text-zinc-500 dark:text-zinc-400">
                Balance today
              </div>
              <div className="mt-1 text-lg font-semibold text-zinc-900 dark:text-white">
                {formatMoney(statement.current_balance)}
              </div>
            </div>
          </div>

          {movedSince && (
            <div className="mt-4 rounded-lg border border-sky-200 dark:border-sky-900 bg-sky-50 dark:bg-sky-950/40 p-4">
              <Text>
                <strong>This statement is backdated.</strong> It closes at{' '}
                {formatMoney(statement.closing_balance)} on{' '}
                {formatBillingDate(statement.to)}, and the account has moved
                since — it stands at {formatMoney(statement.current_balance)}{' '}
                today. Both figures are correct; they answer different
                questions.
              </Text>
            </div>
          )}

          {statement.days_past_due > 0 && (
            <Text className="mt-4">
              {statement.days_past_due} days past due as of today.
            </Text>
          )}

          <Subheading level={4} className="mt-8">
            {statement.hoa_name}
          </Subheading>

          {statement.lines.length === 0 ? (
            <div className="mt-2 rounded-lg border border-dashed border-zinc-300 dark:border-zinc-700 p-6">
              <Text>
                Nothing was posted in this range. The opening and closing
                balances are the same because nothing happened, not because the
                account is empty.
              </Text>
            </div>
          ) : (
            <Table dense className="mt-2">
              <TableHead>
                <TableRow>
                  <TableHeader>Date</TableHeader>
                  <TableHeader>Type</TableHeader>
                  <TableHeader>Description</TableHeader>
                  <TableHeader className="text-right">Amount</TableHeader>
                  <TableHeader className="text-right">Balance</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {statement.lines.map((l) => {
                  const parts = signedAmountParts(l.amount);
                  return (
                    <TableRow key={l.id}>
                      <TableCell>
                        {formatBillingDate(l.effective_date)}
                      </TableCell>
                      <TableCell>
                        <Badge color={ledgerEntryType.color(l.entry_type)}>
                          {ledgerEntryType.label(l.entry_type)}
                        </Badge>
                      </TableCell>
                      <TableCell>{l.description}</TableCell>
                      <TableCell
                        className={
                          parts.isCredit
                            ? 'text-right text-lime-700 dark:text-lime-400'
                            : 'text-right'
                        }
                      >
                        {parts.text}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatMoney(l.running_balance)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </>
      ) : null}
    </div>
  );
};

export default StatementPanel;
